import type { ComponentType, CSSProperties } from 'react';
import {
  AppstoreOutlined,
  BarChartOutlined,
  BookOutlined,
  SafetyCertificateOutlined,
  CloudSyncOutlined,
  DatabaseOutlined,
  FileMarkdownOutlined,
  FolderOutlined,
  FundOutlined,
  GithubOutlined,
  GoogleOutlined,
  HddOutlined,
  HomeOutlined,
  KeyOutlined,
  SearchOutlined,
  SettingOutlined,
  SyncOutlined,
  TeamOutlined,
  TrophyOutlined,
  UserOutlined,
} from '@ant-design/icons';

export type NavIcon = ComponentType<{ style?: CSSProperties }>;

export type NavAccess = 'all' | 'admin' | 'superAdmin';

export const NAV_ADMIN_SUBMENU_ID = 'admin-center';

export interface NavLink {
  kind: 'link';
  id: string;
  path: string;
  label: string;
  description?: string;
  icon: NavIcon;
  access?: NavAccess;
  /** Show on the dashboard launcher grid. */
  shortcut?: boolean;
}

export interface NavGroup {
  kind: 'group';
  id: string;
  label: string;
  children: NavLink[];
}

export interface NavSubmenu {
  kind: 'submenu';
  id: string;
  label: string;
  icon: NavIcon;
  access?: NavAccess;
  children: NavLink[];
}

export type NavNode = NavLink | NavGroup | NavSubmenu;

/**
 * Employee IA by job; admin dump unchanged (order + paths).
 * Paths must stay stable (SAML returnUrl, bookmarks).
 */
export const NAV: NavNode[] = [
  {
    kind: 'link',
    id: '/dashboard',
    path: '/dashboard',
    label: '首页',
    icon: HomeOutlined,
  },
  {
    kind: 'group',
    id: 'ai-dev',
    label: 'AI 与开发',
    children: [
      {
        kind: 'link',
        id: '/ai-access',
        path: '/ai-access',
        label: 'AI 权限管理',
        description: '申请并管理 AI 平台访问权限',
        icon: SafetyCertificateOutlined,
        shortcut: true,
      },
      {
        kind: 'link',
        id: '/ai-tools',
        path: '/ai-tools',
        label: 'AI 工具配置文档',
        description: '终端工具的安装与接入文档',
        icon: BookOutlined,
      },
      {
        kind: 'link',
        id: '/keys',
        path: '/keys',
        label: 'API Key',
        description: 'Habby API 密钥',
        icon: KeyOutlined,
        shortcut: true,
      },
      {
        kind: 'link',
        id: '/github',
        path: '/github',
        label: 'GitHub',
        description: '开通 GitHub Enterprise',
        icon: GithubOutlined,
        shortcut: true,
      },
    ],
  },
  {
    kind: 'group',
    id: 'data-access',
    label: '数据与权限',
    children: [
      {
        kind: 'link',
        id: '/dataset-access',
        path: '/dataset-access',
        label: '数据集',
        description: '申请数据集访问',
        icon: DatabaseOutlined,
        shortcut: true,
      },
      {
        kind: 'link',
        id: '/thinkingdata-access',
        path: '/thinkingdata-access',
        label: 'TGA',
        description: 'ThinkingData 权限',
        icon: BarChartOutlined,
        shortcut: true,
      },
      {
        kind: 'link',
        id: '/marketing-authorizations',
        path: '/marketing-authorizations',
        label: '市场渠道',
        description: '广告与市场渠道授权',
        icon: FundOutlined,
        shortcut: true,
      },
      {
        kind: 'link',
        id: '/scim/my-permissions',
        path: '/scim/my-permissions',
        label: '应用开通',
        description: '开通其它集成应用',
        icon: CloudSyncOutlined,
      },
    ],
  },
  {
    kind: 'link',
    id: '/files',
    path: '/files',
    label: '文件',
    description: '文件与知识库',
    icon: FolderOutlined,
    shortcut: true,
  },
  {
    kind: 'link',
    id: '/onsite-nas',
    path: '/onsite-nas',
    label: 'NAS',
    description: '办公网络存储',
    icon: HddOutlined,
  },
  {
    kind: 'group',
    id: 'help',
    label: '帮助',
    children: [
      {
        kind: 'link',
        id: '/habby-search',
        path: '/habby-search',
        label: 'Habby Search',
        description: '搜索内部知识库',
        icon: SearchOutlined,
      },
      {
        kind: 'link',
        id: '/documents',
        path: '/documents',
        label: '文档',
        description: '内部文档',
        icon: BookOutlined,
        shortcut: true,
      },
      {
        kind: 'link',
        id: '/guides',
        path: '/guides',
        label: '指南',
        description: '使用指南',
        icon: FileMarkdownOutlined,
      },
    ],
  },
  {
    kind: 'submenu',
    id: NAV_ADMIN_SUBMENU_ID,
    label: '管理中心',
    icon: SettingOutlined,
    access: 'admin',
    children: [
      { kind: 'link', id: '/users', path: '/users', label: '用户管理', icon: UserOutlined, access: 'admin' },
      { kind: 'link', id: '/roles', path: '/roles', label: '角色管理', icon: TeamOutlined, access: 'admin' },
      { kind: 'link', id: '/app-management', path: '/app-management', label: 'APP 管理', icon: AppstoreOutlined, access: 'admin' },
      { kind: 'link', id: '/sync-logs', path: '/sync-logs', label: '同步日志', icon: SyncOutlined, access: 'admin' },
      { kind: 'link', id: '/scim/platforms', path: '/scim/platforms', label: 'SCIM 平台配置', icon: CloudSyncOutlined, access: 'admin' },
      { kind: 'link', id: '/github-management', path: '/github-management', label: 'GitHub 管理', icon: GithubOutlined, access: 'admin' },
      { kind: 'link', id: '/dataset-sources', path: '/dataset-sources', label: '数据源管理', icon: DatabaseOutlined, access: 'admin' },
      { kind: 'link', id: '/thinkingdata-admin', path: '/thinkingdata-admin', label: 'TGA 管理', icon: KeyOutlined, access: 'admin' },
      { kind: 'link', id: '/marketing-permissions-admin', path: '/marketing-permissions-admin', label: '市场渠道权限管理', icon: SafetyCertificateOutlined, access: 'admin' },
      { kind: 'link', id: '/games', path: '/games', label: '游戏管理', icon: TrophyOutlined, access: 'admin' },
      { kind: 'link', id: '/game-aliases', path: '/game-aliases', label: '游戏归属', icon: TrophyOutlined, access: 'admin' },
      { kind: 'link', id: '/google-workspace-groups', path: '/google-workspace-groups', label: 'Google Workspace 组管理', icon: GoogleOutlined, access: 'admin' },
      { kind: 'link', id: '/ai-access/zhipuai-pool', path: '/ai-access/zhipuai-pool', label: '智谱 AI Key 池', icon: KeyOutlined, access: 'admin' },
      { kind: 'link', id: '/dictionary', path: '/dictionary', label: '数据字典', icon: BookOutlined, access: 'superAdmin' },
    ],
  },
];
